import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { IconPlugConnected } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  useCreateMilestoneServer,
  useInvalidateMilestoneServers
} from '@/queries/vlmQueries'
import { showToast } from '@/theme/notifications'
import { MilestoneServer } from '@/types/vlm'
import { isAlphanumericWithSpacesHyphensUnderscores } from '@/utils/validation/helpers'
import { isValidUrl } from '@/utils/validation/url'
import { useMilestoneConnectionTest } from './useMilestoneConnectionTest'

export const MILESTONE_SERVER_NAME_MAX_LENGTH = 100

type MilestoneServerFormValues = {
  name: string
  url: string
  username: string
  password: string
}

type MilestoneServerFormProps = {
  deviceId: string
  onCreated: (server: MilestoneServer) => void
  onClose: () => void
}

export const MilestoneServerForm = ({
  deviceId,
  onCreated,
  onClose
}: MilestoneServerFormProps) => {
  const intl = useIntl()

  const { mutateAsync: createServer, isPending } = useCreateMilestoneServer()
  const { invalidateMilestoneServerList } = useInvalidateMilestoneServers()

  const { testConnection, isTesting, result, resetResult } =
    useMilestoneConnectionTest({ deviceId })

  const form = useForm<MilestoneServerFormValues>({
    initialValues: {
      name: '',
      url: '',
      username: '',
      password: ''
    },
    onValuesChange: resetResult,
    validate: {
      name: (value) => {
        if (!value.trim()) {
          return intl.formatMessage({
            id: 'vlm.milestoneServers.form.name.required'
          })
        }

        if (value.length > MILESTONE_SERVER_NAME_MAX_LENGTH) {
          return intl.formatMessage(
            { id: 'vlm.milestoneServers.form.name.maxLength' },
            { max: MILESTONE_SERVER_NAME_MAX_LENGTH }
          )
        }

        if (!isAlphanumericWithSpacesHyphensUnderscores(value)) {
          return intl.formatMessage({
            id: 'vlm.milestoneServers.form.name.invalidChars'
          })
        }

        return null
      },
      url: (value) => {
        if (!value.trim()) {
          return intl.formatMessage({
            id: 'vlm.milestoneServers.form.url.required'
          })
        }

        if (!isValidUrl(value)) {
          return intl.formatMessage({
            id: 'vlm.milestoneServers.form.url.invalid'
          })
        }

        return null
      },
      username: isNotEmpty(
        intl.formatMessage({
          id: 'vlm.milestoneServers.form.username.required'
        })
      ),
      password: isNotEmpty(
        intl.formatMessage({
          id: 'vlm.milestoneServers.form.password.required'
        })
      )
    }
  })

  const handleTestConnection = () => {
    if (form.validate().hasErrors) {
      return
    }

    void testConnection({
      serverUrl: form.values.url,
      username: form.values.username,
      password: form.values.password
    })
  }

  const handleSubmit = async (values: MilestoneServerFormValues) => {
    try {
      const server = await createServer({
        name: values.name,
        server_url: values.url,
        username: values.username,
        password: values.password
      })
      void invalidateMilestoneServerList()
      onCreated(server)
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.milestoneServers.create.error' })
        ),
        'error'
      )
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Paper p="lg" bg="gray.1">
          <Stack gap="md">
            <TextInput
              label={intl.formatMessage({
                id: 'vlm.milestoneServers.form.name.label'
              })}
              placeholder={intl.formatMessage({
                id: 'vlm.milestoneServers.form.name.placeholder'
              })}
              data-autofocus
              {...form.getInputProps('name')}
            />

            <TextInput
              label={intl.formatMessage({
                id: 'vlm.milestoneServers.form.url.label'
              })}
              placeholder={intl.formatMessage({
                id: 'vlm.milestoneServers.form.url.placeholder'
              })}
              {...form.getInputProps('url')}
            />

            <TextInput
              label={intl.formatMessage({
                id: 'vlm.milestoneServers.form.username.label'
              })}
              placeholder={intl.formatMessage({
                id: 'vlm.milestoneServers.form.username.placeholder'
              })}
              {...form.getInputProps('username')}
            />

            <PasswordInput
              label={intl.formatMessage({
                id: 'vlm.milestoneServers.form.password.label'
              })}
              {...form.getInputProps('password')}
            />

            <PlatformAlert
              variant="info"
              title={
                <FormattedMessage id="vlm.milestoneServers.form.testConnection.hint.title" />
              }
              compact
            >
              <FormattedMessage id="vlm.milestoneServers.form.testConnection.hint" />
            </PlatformAlert>

            <Stack gap="xs">
              <Group>
                <Tooltip
                  label={
                    <FormattedMessage id="vlm.milestoneServers.form.testConnection.noDevice" />
                  }
                  disabled={!!deviceId}
                >
                  <Button
                    type="button"
                    size="xs"
                    miw={100}
                    color="blue.8"
                    loading={isTesting}
                    disabled={!deviceId}
                    leftSection={<IconPlugConnected size={16} />}
                    onClick={handleTestConnection}
                  >
                    <FormattedMessage id="vlm.milestoneServers.form.testConnection" />
                  </Button>
                </Tooltip>
              </Group>

              {result && (
                <Text
                  size="xs"
                  c={result.status === 'success' ? 'green.7' : 'red.7'}
                >
                  {result.message}
                </Text>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} type="submit">
            <FormattedMessage id="vlm.milestoneServers.create.add" />
          </Button>
        </Group>
      </Box>
    </form>
  )
}
